import { FC, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { TopBar } from "components/navigation/TopBar"
import {
  ACTION_LABELS,
  actionFields,
  CONDITION_LABELS,
  conditionFields,
  formatLabel,
  MODIFIER_LABELS,
  modifierFields,
  renderFields,
  STATUS_COLORS
} from "components/tournaments/constants"

import { useTimezone } from "hooks/useTimezone"

import { URL } from "constants/api/requests"

import { TournamentService } from "services/tournaments/tournament.service"

import { Tournament } from "types/tournament.types"

import { formatTournamentMoney, tournamentDisplayMinor, usdCentsToCurrencyMinor } from "util/formatter"

export const TournamentDetail: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation("translation", { useSuspense: false })
  const { formatDateTime } = useTimezone()

  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return

    TournamentService.getTournament(id)
      .then((data) => {
        setTournament(data || null)
      })
      .catch(() => {
        toast.error("Failed to load tournament!")
      })
      .finally(() => {
        setLoading(false)
      })
  }, [id])

  if (loading) {
    return (
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.tournamentDetail.title")} />
        <div className="flex items-center justify-center py-16">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    )
  }

  if (!tournament) {
    return (
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.tournamentDetail.title")} />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">{t("tournaments.notFound")}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
      <TopBar title="Tournament Details" />

      <div className="mt-4">
        <div className="p-6 bg-white dark:bg-[#0C0C1A] rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] shadow-sm">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{tournament.name}</h2>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${STATUS_COLORS[tournament.status]}`}>
                  {tournament.status.toLowerCase()}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">ID: {tournament.id}</span>
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => navigate(URL.TOURNAMENTS)}
                className="px-4 py-2 text-sm border border-[rgba(139,92,246,0.8)] dark:border-[rgba(139,92,246,0.5)] rounded-lg hover:bg-[rgba(139,92,246,0.1)] dark:hover:bg-[rgba(139,92,246,0.1)] text-[rgba(139,92,246,1)] dark:text-white transition-all"
              >
                {t("tournaments.backToList")}
              </button>
              {tournament.status !== "DRAFT" && (
                <button
                  onClick={() => navigate(URL.TOURNAMENT_MONITOR.replace(":id", tournament.id))}
                  className="px-4 py-2 text-sm bg-[rgba(139,92,246,1)] text-white rounded-lg hover:bg-[rgba(139,92,246,0.8)] transition-all"
                >
                  {t("tournaments.monitor")}
                </button>
              )}
            </div>
          </div>

          {/* Description */}
          {tournament.description && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-[rgba(139,92,246,0.05)] rounded-lg border border-[rgba(0,0,0,0.05)] dark:border-[rgba(139,92,246,0.2)]">
              <p className="text-gray-700 dark:text-gray-300">{tournament.description}</p>
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Schedule */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t("tournaments.detail.schedule")}</h3>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.startTime")}</div>
                <div className="text-gray-900 dark:text-white">{formatDateTime(tournament.start_time)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.endTime")}</div>
                <div className="text-gray-900 dark:text-white">{formatDateTime(tournament.end_time)}</div>
              </div>
            </div>

            {/* Configuration */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t("tournaments.detail.configuration")}</h3>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.scoringMode")}</div>
                <div className="text-gray-900 dark:text-white capitalize">{tournament.scoring_mode.toLowerCase().replace("_", " ")}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.optInRequired")}</div>
                <div className="text-gray-900 dark:text-white">{tournament.opt_in_required ? t("common.yes") : t("common.no")}</div>
              </div>
              {tournament.min_bet && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.minimumBet")}</div>
                  <div className="text-gray-900 dark:text-white">
                    {formatTournamentMoney(
                      tournamentDisplayMinor(tournament, "min_bet_per_spin", tournament.min_bet),
                      tournament.currency
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Prize Pool */}
            {tournament.prize_pool_type && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">{t("tournaments.detail.prizePool")}</h3>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.type")}</div>
                  <div className="text-gray-900 dark:text-white capitalize">
                    {tournament.prize_pool_type.toLowerCase().replace("_", " ")}
                  </div>
                </div>
                {tournament.prize_pool_base && (
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.baseAmount")}</div>
                    <div className="text-gray-900 dark:text-white font-semibold">
                      {formatTournamentMoney(
                        tournamentDisplayMinor(tournament, "prize_pool_base", tournament.prize_pool_base),
                        tournament.currency
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Eligibility */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t("tournaments.detail.eligibility")}</h3>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.maxParticipants")}</div>
                <div className="text-gray-900 dark:text-white">{tournament.max_participants || t("common.unlimited")}</div>
              </div>
              {tournament.eligible_games && Array.isArray(tournament.eligible_games) && tournament.eligible_games.length > 0 && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.eligibleGames")}</div>
                  <div className="text-gray-900 dark:text-white text-xs">
                    {t("tournaments.detail.gameCount", { count: tournament.eligible_games.length })}
                  </div>
                </div>
              )}
              {tournament.eligible_providers &&
                Array.isArray(tournament.eligible_providers) &&
                tournament.eligible_providers.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t("tournaments.detail.eligibleProviders")}</div>
                    <div className="text-gray-900 dark:text-white text-xs">{tournament.eligible_providers.join(", ")}</div>
                  </div>
                )}
            </div>
          </div>

          {/* Prize Distribution */}
          {tournament.prize_distribution && Array.isArray(tournament.prize_distribution) && tournament.prize_distribution.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t("tournaments.detail.prizeDistribution")}</h3>
              {tournament.prizes_distributed_at && (
                <div className="mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg font-medium">
                  {t("tournaments.detail.prizesDistributedAt", { date: formatDateTime(tournament.prizes_distributed_at) })}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] rounded-lg">
                  <thead className="bg-gray-50 dark:bg-[rgba(139,92,246,0.05)]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
                        Rank
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
                        Prize
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#0C0C1A] divide-y divide-[rgba(0,0,0,0.05)] dark:divide-[rgba(255,255,255,0.1)]">
                    {tournament.prize_distribution.map((prize, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-[rgba(139,92,246,0.05)] transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">#{prize.rank}</span>
                          {index < 3 && <span className="ml-2 text-lg">{index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {prize.amount !== undefined && prize.amount !== null ? (
                            <span className="font-semibold text-green-600 dark:text-green-400">
                              {formatTournamentMoney(
                                usdCentsToCurrencyMinor(prize.amount, tournament.currency_rate),
                                tournament.currency
                              )}
                            </span>
                          ) : prize.percentage !== undefined && prize.percentage !== null ? (
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                              {prize.percentage}% {t("tournaments.detail.ofPool")}
                            </span>
                          ) : (
                            <span className="text-gray-500">{t("common.notSpecified")}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Point Rules */}
          {tournament.point_rules && Array.isArray(tournament.point_rules) && tournament.point_rules.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t("tournaments.detail.pointRules")}</h3>
              <div className="space-y-4">
                {tournament.point_rules.map((rule, index) => (
                  <div
                    key={rule.id || index}
                    className={`p-4 bg-gray-50 dark:bg-[rgba(139,92,246,0.05)] rounded-lg border border-[rgba(0,0,0,0.05)] dark:border-[rgba(139,92,246,0.2)] ${
                      rule.enabled === false ? "opacity-50" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">{rule.name}</h4>
                        {rule.enabled === false && (
                          <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            {t("common.disabled")}
                          </span>
                        )}
                      </div>
                      <span className="text-xs px-2 py-1 bg-[rgba(139,92,246,0.1)] text-[rgba(139,92,246,1)] dark:text-white rounded">
                        Rule #{index + 1}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* IF Condition */}
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          {t("tournaments.detail.condition")}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <div>
                            Type: <span className="font-medium">{formatLabel(CONDITION_LABELS, rule.if?.type)}</span>
                          </div>
                          {renderFields(rule.if, conditionFields)}
                        </div>
                      </div>

                      {/* THEN Action */}
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          {t("tournaments.detail.action")}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <div>
                            Type: <span className="font-medium">{formatLabel(ACTION_LABELS, rule.then?.type)}</span>
                          </div>
                          {renderFields(rule.then, actionFields)}
                        </div>
                      </div>

                      {/* Modifier */}
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          {t("tournaments.detail.modifier")}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <div>
                            Type: <span className="font-medium">{formatLabel(MODIFIER_LABELS, rule.modifier?.type)}</span>
                          </div>
                          {renderFields(rule.modifier, modifierFields)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="mt-6 pt-6 border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <div>
              {t("tournaments.detail.created")} {formatDateTime(tournament.created_at)}
            </div>
            <div>
              {t("tournaments.detail.updated")} {formatDateTime(tournament.updated_at)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
